import { invertCommand, type Command } from "../document/commands.ts";
import { HISTORY_CAP, type History } from "../document/history.ts";
import type { WorldDocument } from "../document/types.ts";
import { commandToOps } from "../live/command-ops.ts";
import type { LiveServerMessage } from "../live/protocol.ts";
import type { FrozenReason } from "../live/protocol-stream.ts";
import { liveRepaintOf } from "../live/repaint.ts";
import type { CheckResult } from "../live/session-check.ts";
import type { CheckVerdict, EditEntry, LiveSession, SessionCheck, SessionRefusal, SessionStep, UndoStep } from "../live/session-types.ts";
import { adoptSnapshot, checkCompared, checksEnded, editRefusal, opsRefusal, pollSession, queued, receiveServerMessage, sessionIds, sessionSaveState, stepOf, streamLost, takeLocalOps, type SessionSnapshot } from "../live/session.ts";
import { redoEdit, undoEdit } from "../live/session-undo.ts";
import type { LiveDeps, LiveEditsHost, LiveRefusal, LiveStatus } from "./live-edits-types.ts";
import { loadCommands } from "./live-load.ts";

export type { LiveDeps, LiveEditsHost, LiveEvent, LiveOutput, LiveRefusal, LiveStatus } from "./live-edits-types.ts";

/**
 * A store's session (ADR-086 decisions 26 and 27, P1.11-T28): the one place a store's commands, its undo and
 * redo and the frames of its host's socket meet the tab's `LiveSession`. The store shows the session's shown
 * document and nothing else: a local command is checked, turned into operations and taken by the session
 * before the store shows it; a frame from the server moves the session and the store adopts what it then
 * shows. It holds no socket and no timer: the host hands it the clock, sends the frames each step returns,
 * polls again at the time it is told, and reads a snapshot when asked.
 *
 * While a stroke is held, what the server sends waits here and is taken when the stroke has ended, so the
 * stroke's command still applies to the document it was painted on (decision 26).
 *
 * A tab takes no command before it has been live in the stream once (decision 17: a tab that has not joined
 * is view-only). The first time it is live, a tab that may write sends what a load changes outside the history
 * (tidied terrain names, the host's upgrade; a load no longer adds the catalogue's tilesets, ADR-129 A4 rule 1) as its first operations, built on the
 * document it has then caught up to and kept out of its undo list; a tab that may only view sends nothing and
 * shows the server's document as it is.
 *
 * The divergence check (decision 33) passes through here as data: a step's output says when a checkpoint
 * waits, the host reads it with `check`, hashes its document when the editor is idle and hands the digest to
 * `checked`. Nothing is hashed here, and nothing of it runs for an edit or a frame.
 */

/**
 * Something the host handed over that waits for its turn.
 */
type LiveInput = { readonly kind: "message"; readonly message: LiveServerMessage } | { readonly kind: "lost" } | { readonly kind: "snapshot"; readonly snapshot: SessionSnapshot };

/**
 * One command standing for an edit of the tab's own, for the History drawer's words.
 *
 * @param entry The edit.
 * @returns Its commands as one: the command itself when it is one, a compound otherwise.
 */
function commandOf(entry: EditEntry): Command {
  const commands = entry.parts.flatMap((part) => part.commands);
  return commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands };
}

/**
 * What of a session the store's subscribers can see beside the document: the counts the menu reads and where
 * the session stands. A step that changes none of it (a frame sent, an answer to an operation, which changes
 * only how many are on their way) tells them nothing: a host reads `status` when it draws, and an answer a
 * stroke would otherwise cost every panel a render.
 *
 * @param session The session.
 * @returns The text two sessions share when a subscriber cannot tell them apart.
 */
function seenOf(session: LiveSession): string {
  return `${session.link} ${session.stop ?? ""} ${session.past.length} ${session.future.length}`;
}

/**
 * A store's session.
 */
export class LiveEdits {
  private held: LiveSession | null = null;
  private deps: LiveDeps | null = null;
  private readonly inbox: LiveInput[] = [];
  private frozen: FrozenReason | null = null;
  private droppedEdits = 0;
  private refused = 0;
  private everLive = false;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: LiveEditsHost) {}

  /**
   * The session as it stands.
   *
   * @returns The session, or null for a store with none.
   */
  public get session(): LiveSession | null {
    return this.held;
  }

  /**
   * Whether the store has a session.
   *
   * @returns True once `attach` ran.
   */
  public get attached(): boolean {
    return this.held !== null;
  }

  /**
   * Where the store stands with its session.
   *
   * @returns The status, or null for a store with no session.
   */
  public get status(): LiveStatus | null {
    const session = this.held;
    const deps = this.deps;
    if (session === null || deps === null) {
      return null;
    }
    const edits = new Set(session.pending.map((held) => held.op.group));
    const sent = new Set(session.pending.filter((held) => held.sent || held.resent).map((held) => held.op.group));
    return { save: sessionSaveState(session, deps.now()), link: session.link, stop: session.stop, writer: session.writer, joined: this.everLive, refusal: this.refusal(), pending: session.pending.length, edits: edits.size, unsent: edits.size - sent.size, frozen: this.frozen };
  }

  /**
   * How many of the tab's own edits its history has let go: past the cap, and for a tileset that moved.
   *
   * @returns The count.
   */
  public get dropped(): number {
    return this.droppedEdits;
  }

  /**
   * How many commands, strokes, undos and redos the session has refused since it was attached: what a panel
   * reads before and after a commit to know whether to keep what was typed.
   *
   * @returns The count; 0 for a store with no session.
   */
  public get refusals(): number {
    return this.refused;
  }

  /**
   * The tab's own edits as the History drawer lists them: each edit as one command, an undone one as the edit
   * it was, not as what its undo did.
   *
   * @returns The done and undone edits, the newest last.
   */
  public get history(): History {
    const session = this.held;
    return session === null ? { past: [], future: [] } : { past: session.past.map(commandOf), future: session.future.map((entry) => invertCommand(commandOf(entry))) };
  }

  /**
   * Gives the store a session. The store shows the session's document as it is, value for value, and takes no
   * command until the session has been live in the stream once.
   *
   * @param session The session, opened over the document the host loaded.
   * @param deps The clock and where the output and the events go.
   * @throws Error when the store already has a session, a stroke is held, or the session's tag is not one the
   *   store mints ids under.
   */
  public attach(session: LiveSession, deps: LiveDeps): void {
    if (this.held !== null) {
      throw new Error("the store already has a session");
    }
    this.host.tag(session.tag);
    this.held = session;
    this.deps = deps;
    if (!this.host.adopt(session.shown, [{ kind: "world" }])) {
      this.held = null;
      this.deps = null;
      throw new Error("a session cannot be attached while a stroke is held");
    }
  }

  /**
   * The session with what a load changes outside the history queued as operations of the tab's own, outside
   * its undo list (decision 26): tidied terrain names and the host's upgrade, built
   * on the document the session shows.
   *
   * @param session The session, live for the first time.
   * @returns The session with those operations pending, or the session as given when the tab may only view,
   *   the load changes nothing, or the changes cannot travel.
   */
  private withLoad(session: LiveSession): LiveSession {
    const deps = this.deps;
    if (deps === null || !session.writer || session.stop !== null) {
      return session;
    }
    const opened = this.host.opened(session.shown);
    const commands = loadCommands(session.shown, deps.upgrade?.(opened) ?? opened);
    const ops = commands.length === 0 ? [] : commandToOps({ kind: "compound", commands }, session.shown, sessionIds(session));
    return ops === null || ops.length === 0 || opsRefusal(session, ops, deps.now()) !== null ? session : queued(session, ops).session;
  }

  /**
   * Takes a frame the server sent, in its turn.
   *
   * @param message The frame, as `parseServerMessage` read it.
   */
  public receive(message: LiveServerMessage): void {
    this.inbox.push({ kind: "message", message });
    this.settle();
  }

  /**
   * Keeps a frame the server sent for the next `settle`, `poll`, `lost` or `snapshot`: what a host calls for
   * each frame when it takes several at once, a frame of the display, so that a burst of operations is adopted
   * and announced once.
   *
   * @param message The frame, as `parseServerMessage` read it.
   */
  public queue(message: LiveServerMessage): void {
    this.inbox.push({ kind: "message", message });
  }

  /**
   * Notes that the socket closed, in its turn.
   */
  public lost(): void {
    this.inbox.push({ kind: "lost" });
    this.settle();
  }

  /**
   * Takes the snapshot the host read, in its turn.
   *
   * @param snapshot The room's document with its epoch and sequence.
   */
  public snapshot(snapshot: SessionSnapshot): void {
    this.inbox.push({ kind: "snapshot", snapshot });
    this.settle();
  }

  /**
   * The oldest checkpoint that waits for the host to hash its document.
   *
   * @returns The check with the confirmed document it holds, or null when none waits.
   */
  public get check(): SessionCheck | null {
    return this.held?.checks[0] ?? null;
  }

  /**
   * Whether a stroke is held: the host hashes nothing while one is.
   *
   * @returns True while the store holds a stroke.
   */
  public get busy(): boolean {
    return this.host.held();
  }

  /**
   * Takes the digest the host computed for a check. A difference is told as a `diverged` event with the
   * sequence, and the step's output asks for the snapshot that replaces the confirmed document; what the tab
   * shows changes only when that snapshot is adopted.
   *
   * @param result The check's epoch and sequence with the SHA-256 of the text of the document it held.
   * @returns What became of the check, or null for a store with no session.
   */
  public checked(result: CheckResult): CheckVerdict | null {
    const session = this.held;
    const deps = this.deps;
    if (session === null || deps === null) {
      return null;
    }
    const step = checkCompared(session, result, deps.now());
    if (step.verdict === "stale") {
      return step.verdict;
    }
    if (step.diverged !== null) {
      deps.event({ kind: "diverged", seq: step.diverged });
    }
    this.advance([step], session.shown);
    return step.verdict;
  }

  /**
   * Notes that the host hashes no more checkpoints: the session lets go the documents it kept for them and
   * keeps none from here on, and the output says none waits.
   */
  public endChecks(): void {
    const session = this.held;
    if (session !== null && session.checking) {
      this.held = checksEnded(session);
      this.emit([], this.held);
    }
  }

  /**
   * Takes what waited and sends the next operation that is due: what the host calls at the time a step's
   * `wake` named, and whenever it likes besides. A call with nothing due changes nothing and tells nobody.
   */
  public poll(): void {
    this.settle();
    const session = this.held;
    const deps = this.deps;
    if (session === null || deps === null) {
      return;
    }
    this.advance([pollSession(session, deps.now())], session.shown);
  }

  /**
   * Takes everything that waited for its turn, unless a stroke is held: the session moves a step an item, and
   * the store adopts what it then shows once, with what differs drawn again.
   */
  public settle(): void {
    const first = this.held;
    const deps = this.deps;
    if (first === null || deps === null || this.inbox.length === 0 || this.host.held()) {
      return;
    }
    const steps: SessionStep[] = [];
    let session = first;
    for (const input of this.inbox.splice(0)) {
      const now = deps.now();
      if (input.kind === "message" && input.message.t === "frozen") {
        this.frozen = this.frozen ?? input.message.reason;
      }
      const step = input.kind === "message" ? receiveServerMessage(session, input.message, now) : input.kind === "snapshot" ? adoptSnapshot(session, input.snapshot, now) : stepOf(streamLost(session, now));
      steps.push(step);
      session = step.session;
    }
    this.advance(steps, first.shown);
  }

  /**
   * Why the session takes no command now.
   *
   * @returns The reason, or null when it takes one or the store has no session: the session's own
   *   (`editRefusal`), and `offline` for a tab that has not been live in the stream yet.
   */
  public refusal(): SessionRefusal | null {
    if (this.held === null || this.deps === null) {
      return null;
    }
    return editRefusal(this.held, this.deps.now()) ?? (this.everLive ? null : "offline");
  }

  /**
   * Tells the host a command or a stroke was refused.
   *
   * @param reason Why.
   */
  public refuse(reason: LiveRefusal): void {
    this.refused += 1;
    this.deps?.event({ kind: "refused", reason });
  }

  /**
   * Tells the host the store's own result of a command differed from the session's.
   */
  public mismatch(): void {
    this.deps?.event({ kind: "mismatch" });
  }

  /**
   * Takes a local command into the session: its operations are built from the shown document, and the command
   * is refused, nothing changed and the reason told, when the session takes no command, when it cannot travel
   * as operations, or when any intent of it would be skipped on the shown document. The first of its operations
   * is handed to the host to send in the same turn when the session's pace allows, before whatever the edit
   * costs the page to draw.
   *
   * @param command The command, which applies to the shown document.
   * @returns The document the session shows with the command's operations pending, the shown document itself
   *   for a command that writes nothing, or null when the command was refused.
   * @throws Error when the store has no session.
   */
  public take(command: Command): WorldDocument | null {
    const session = this.held;
    const deps = this.deps;
    if (session === null || deps === null) {
      throw new Error("the store has no session");
    }
    const now = deps.now();
    const refused = this.refusal();
    if (refused !== null) {
      this.refuse(refused);
      return null;
    }
    const ops = commandToOps(command, session.shown, sessionIds(session));
    if (ops === null) {
      this.refuse("travel");
      return null;
    }
    if (ops.length === 0) {
      return session.shown;
    }
    const step = takeLocalOps(session, ops, now);
    const skipped = step.outcomes.flat().find((outcome) => outcome.status === "skipped");
    if (step.refused !== null || skipped !== undefined) {
      this.refuse(step.refused ?? (skipped?.status === "skipped" ? skipped.reason : "travel"));
      return null;
    }
    this.keep(session, step);
    const sent = pollSession(step.session, now);
    this.held = sent.session;
    this.emit([step, sent]);
    return step.session.shown;
  }

  /**
   * Undoes or redoes the tab's own newest edit that still can be, and tells the host what became of it.
   *
   * @param redo Whether to redo.
   * @returns The step when it put anything back, for the store to adopt; null when nothing changed (refused,
   *   nothing to take back, or everything it touched is changed or gone).
   * @throws Error when the store has no session.
   */
  public turn(redo: boolean): UndoStep | null {
    const session = this.held;
    const deps = this.deps;
    if (session === null || deps === null) {
      throw new Error("the store has no session");
    }
    const refused = this.refusal();
    const step = refused === null ? (redo ? redoEdit(session, deps.now()) : undoEdit(session, deps.now())) : null;
    if (step === null || step.status === null) {
      this.refuse(refused ?? step?.refused ?? "travel");
      return null;
    }
    this.keep(session, step);
    deps.event({ kind: redo ? "redo" : "undo", status: step.status, kept: step.kept, skipped: step.skipped, passed: step.passed });
    this.emit([step]);
    if (step.status === "done" || step.status === "partial") {
      return step;
    }
    this.host.notify();
    return null;
  }

  /**
   * Keeps a step's session and counts the edits its history let go.
   *
   * @param before The session before the step.
   * @param step The step.
   */
  private keep(before: LiveSession, step: SessionStep): void {
    const capped = before.past.length === HISTORY_CAP && step.session.past.length === HISTORY_CAP && before.past[0] !== step.session.past[0] ? 1 : 0;
    this.held = step.session;
    this.droppedEdits += capped + step.historyDropped;
    if (step.historyDropped > 0) {
      this.deps?.event({ kind: "history", dropped: step.historyDropped });
    }
  }

  /**
   * Keeps the last of several steps, has the store adopt what the session then shows, and hands the host what
   * the steps send. The first time the session is live, what a load changes is queued behind them. The
   * store's subscribers hear only of what they can see: steps that changed what is shown are adopted, ones
   * that changed a count or where the session stands notify, and a step that only sent a frame tells nobody.
   *
   * @param steps The steps, in order, each on the session the one before it left.
   * @param shown The document shown before the first.
   */
  private advance(steps: readonly SessionStep[], shown: WorldDocument): void {
    const first = this.held;
    let session = first;
    for (const step of steps) {
      if (session !== null) {
        this.keep(session, step);
      }
      session = step.session;
      const lost = step.unkept[0];
      if (lost !== undefined) {
        this.deps?.event({ kind: "lost", reason: lost });
      }
    }
    if (first === null || session === null) {
      return;
    }
    let loaded = false;
    if (!this.everLive && session.link === "live") {
      this.everLive = true;
      const withLoad = this.withLoad(session);
      loaded = withLoad !== session;
      session = withLoad;
      this.held = session;
    }
    if (session.shown !== shown) {
      const last = steps[steps.length - 1];
      this.host.adopt(session.shown, steps.length === 1 && last !== undefined && !loaded ? last.repaint : liveRepaintOf(shown, session.shown));
    } else if (seenOf(session) !== seenOf(first) || loaded) {
      this.host.notify();
    }
    this.emit(steps, session);
  }

  /**
   * Hands the host what steps send, when to poll next, whether a snapshot is wanted and whether a checkpoint
   * waits to be hashed.
   *
   * @param steps The steps, in order.
   * @param session The session they left, the last step's unless more was queued behind it.
   */
  private emit(steps: readonly SessionStep[], session: LiveSession | null = steps[steps.length - 1]?.session ?? null): void {
    if (session !== null) {
      const wanted = session.link === "snapshot" && session.stop === null && !this.inbox.some((input) => input.kind === "snapshot");
      this.deps?.out({ send: steps.flatMap((step) => step.send), wake: stepOf(session).wake, snapshot: wanted, check: session.checks.length > 0 });
    }
  }
}
